// Coastal Breeze Theme Color Scheme for ClipKite
// Single theme with light/dark variants - WCAG AA compliant
// Based on: Charcoal, Persian Green, Saffron, Sandy Brown, Burnt Sienna

export const THEMES = {
  COASTAL_BREEZE: 'coastal-breeze'
};

export const MODES = {
  LIGHT: 'light',
  DARK: 'dark'
};

export const colorSchemes = {
  [THEMES.COASTAL_BREEZE]: {
    name: 'Coastal Breeze',
    description: 'Sophisticated earthy tones with coastal inspiration',
    icon: '🌊',
    [MODES.LIGHT]: {
      primary: '#2a9d8f',        // persian_green
      primaryHover: '#217e73',   // persian_green 400
      primaryActive: '#195e56',  // persian_green 300
      secondary: '#e9c46a',      // saffron
      accent: '#f4a261',         // sandy_brown
      background: '#f8f9fa',     // neutral light gray
      backgroundSecondary: '#f1f3f5', // slightly darker neutral
      backgroundTertiary: '#e9ecef',  // even darker neutral
      surface: '#FFFFFF',
      surfaceHover: '#f8f9fa',   // subtle hover
      text: '#172b32',           // charcoal 300 (darker for contrast)
      textSecondary: '#264653',  // charcoal
      textMuted: '#609db6',      // charcoal 700
      border: '#dee2e6',         // neutral border
      borderHover: '#adb5bd',    // darker neutral border
      success: '#2a9d8f',        // persian_green
      warning: '#e9c46a',        // saffron
      error: '#e76f51',          // burnt_sienna
      info: '#3f7489',           // charcoal 600
      // Video specific
      videoOverlay: 'rgba(38, 70, 83, 0.5)', // charcoal with opacity
      videoControls: 'rgba(255, 255, 255, 0.95)',
      videoProgress: '#2a9d8f',  // persian_green
      appGradient: '#f8f9fa',    // solid neutral background
      appGradientOverlay: 'transparent'
    },
    [MODES.DARK]: {
      primary: '#6366f1',        // indigo for Slate Blue theme
      primaryHover: '#4f46e5',   // darker indigo
      primaryActive: '#4338ca',  // even darker indigo
      secondary: '#475569',      // slate gray
      accent: '#a855f7',         // purple accent
      background: '#0f172a',     // slate-900 background
      backgroundSecondary: '#1e293b', // slate-800
      backgroundTertiary: '#334155',  // slate-700
      surface: '#1e293b',        // slate-800 for cards
      surfaceHover: '#334155',   // slate-700 for hover
      text: '#f8fafc',           // slate-50 for text
      textSecondary: '#e2e8f0',  // slate-200
      textMuted: '#94a3b8',      // slate-400
      border: '#334155',         // slate-700 border
      borderHover: '#475569',    // slate-600 border hover
      success: '#10b981',        // green
      warning: '#f59e0b',        // amber
      error: '#ef4444',          // red
      info: '#6366f1',           // indigo
      videoOverlay: 'rgba(15, 23, 42, 0.7)', // slate-900 with opacity
      videoControls: 'rgba(30, 41, 59, 0.95)', // slate-800 with opacity
      videoProgress: '#6366f1',  // indigo
      appGradient: '#0f172a',    // slate-900 background
      appGradientOverlay: 'transparent'
    }
  }
};

// Generate CSS custom properties for a theme
export const generateCSSVariables = (theme, mode) => {
  const colors = colorSchemes[theme][mode];
  const variables = {};

  Object.entries(colors).forEach(([key, value]) => {
    const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    variables[cssVarName] = value;
  });

  return variables;
};

// Default theme and mode
export const DEFAULT_THEME = THEMES.COASTAL_BREEZE;
export const DEFAULT_MODE = MODES.DARK;

// Utility to get system preference
export const getSystemPreference = () => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? MODES.DARK
      : MODES.LIGHT;
  }
  return DEFAULT_MODE;
};
